import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Shield, HardHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const empty = { name: "", email: "", phone: "", password: "", role: "operario" };

export default function Team() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editId, setEditId] = useState(null);
  const me = JSON.parse(localStorage.getItem("gr_user") || "{}");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name || !editing.email || (!editId && !editing.password)) { toast.error("Completá nombre, email y contraseña"); return; }
    try {
      if (editId) {
        const { data } = await api.put(`/users/${editId}`, editing);
        if (editId === me.id) localStorage.setItem("gr_user", JSON.stringify({ ...me, name: data.name, email: data.email }));
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", editing);
        toast.success("Usuario creado");
      }
      setEditing(null);
      setEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); load(); toast.success("Usuario eliminado"); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="team-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-heading font-bold uppercase text-2xl tracking-tight">Equipo y roles</h1>
        <Button onClick={() => { setEditing({ ...empty }); setEditId(null); }} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="new-user-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo usuario
        </Button>
      </div>
      <p className="text-sm text-neutral-400 mb-6">Los <b>administradores</b> manejan todo el panel. Los <b>operarios</b> solo ven sus órdenes de trabajo asignadas (sin precios) desde su propio dashboard.</p>

      <div className="bg-[#171717] border border-neutral-800 rounded-md overflow-hidden">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-neutral-800 last:border-0 hover:bg-neutral-900/50" data-testid={`user-row-${u.id}`}>
            <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${u.role === "admin" ? "bg-[#F2B90D]/15 border border-[#F2B90D]/40" : "bg-blue-500/10 border border-blue-500/30"}`}>
              {u.role === "admin" ? <Shield className="w-4.5 h-4.5 text-[#F2B90D]" /> : <HardHat className="w-4.5 h-4.5 text-blue-400" />}
            </div>
            <div className="flex-1 min-w-[160px]">
              <p className="text-sm font-medium">{u.name} {u.id === me.id && <span className="text-xs text-neutral-500">(vos)</span>}</p>
              <p className="text-xs text-neutral-500">{u.email}</p>
              <p className={`text-xs ${u.phone ? "text-green-500" : "text-amber-500"}`}>
                {u.phone ? `WhatsApp: +${u.phone}` : "Falta WhatsApp para avisos dobles"}
              </p>
            </div>
            <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-sm border ${u.role === "admin" ? "text-[#F2B90D] border-[#F2B90D]/40 bg-[#F2B90D]/10" : "text-blue-400 border-blue-500/40 bg-blue-500/10"}`}>
              {u.role}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing({ name: u.name, email: u.email, phone: u.phone || "", password: "", role: u.role }); setEditId(u.id); }} className="border-neutral-700 rounded-sm h-8 px-2 hover:bg-neutral-800 hover:text-white" data-testid={`user-edit-${u.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              {u.id !== me.id && (
                <Button size="sm" variant="outline" onClick={() => remove(u.id)} className="border-neutral-700 rounded-sm h-8 px-2 text-red-400 hover:bg-red-500/10 hover:text-red-400" data-testid={`user-delete-${u.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditId(null); } }}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editId ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input placeholder="Nombre *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="user-name-input" />
              <Input placeholder="Email *" type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="user-email-input" />
              <Input placeholder="WhatsApp con código de país, ej. 549221..." value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="user-phone-input" />
              <Input placeholder={editId ? "Nueva contraseña (dejar vacío para no cambiar)" : "Contraseña *"} type="text" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="user-password-input" />
              <div>
                <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Rol</label>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm" data-testid="user-role-select">
                  <option value="operario">Operario — solo ve sus órdenes asignadas</option>
                  <option value="admin">Administrador — acceso total al panel</option>
                </select>
              </div>
              <Button onClick={save} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="user-save-btn">{editId ? "Guardar cambios" : "Crear usuario"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
